import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Stack,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  EventBusy,
  AccessTime,
  TrendingUp,
  TrendingDown,
  Person,
  CalendarToday,
  Work,
  AssignmentLate,
} from '@mui/icons-material';

const MonthlyReportCard = ({ reportData, selectedYear, selectedMonth, getMonthName, formatWorkingHours }) => {
  if (!reportData || !reportData.summary || reportData.summary.length === 0) {
    return (
      <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
        <CardContent>
          <Typography sx={{ color: '#B4C4A8', textAlign: 'center' }}>
            No attendance data available for {getMonthName(selectedMonth)} {selectedYear}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const monthTotals = reportData.monthTotals || {};
  const totalEmployees = reportData.summary.length;
  const totalWorkingDays = monthTotals.totalDays || new Date(selectedYear, selectedMonth + 1, 0).getDate();

  // Calculate department-wise summary
  const departmentSummary = {};
  reportData.summary.forEach(emp => {
    const dept = emp.employee?.department || 'Unassigned';
    if (!departmentSummary[dept]) {
      departmentSummary[dept] = {
        totalEmployees: 0,
        totalPresent: 0,
        totalLate: 0,
        totalAbsent: 0,
        totalWorkingHours: 0,
      };
    }
    departmentSummary[dept].totalEmployees++;
    departmentSummary[dept].totalPresent += (emp.daysPresent || 0);
    departmentSummary[dept].totalLate += (emp.daysLate || 0);
    departmentSummary[dept].totalAbsent += (emp.daysAbsent || 0);
    departmentSummary[dept].totalWorkingHours += (emp.totalWorkingMinutes || 0);
  });

  // Top performers (most present days)
  const topPerformers = [...reportData.summary]
    .sort((a, b) => (b.daysPresent || 0) - (a.daysPresent || 0))
    .slice(0, 5);

  // Employees needing attention (most absent days)
  const needingAttention = [...reportData.summary]
    .sort((a, b) => (b.daysAbsent || 0) - (a.daysAbsent || 0))
    .slice(0, 5);

  return (
    <Box>
      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Total Employees</Typography>
                  <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                    {totalEmployees}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#D4AF37', 0.2), color: '#D4AF37' }}>
                  <Person />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Total Present Days</Typography>
                  <Typography variant="h4" sx={{ color: '#81C784', fontWeight: 700 }}>
                    {monthTotals.totalPresent || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Total Absent Days</Typography>
                  <Typography variant="h4" sx={{ color: '#EF9A9A', fontWeight: 700 }}>
                    {monthTotals.totalAbsent || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A' }}>
                  <Cancel />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Attendance Rate</Typography>
                  <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                    {totalEmployees > 0 ? Math.round(((monthTotals.totalPresent || 0) / (totalEmployees * totalWorkingDays)) * 100) : 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#D4AF37', 0.2), color: '#D4AF37' }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Department Summary Section */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#D4AF37' }}>
        Department-wise Summary
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(departmentSummary).map(([dept, data]) => {
          const attendanceRate = (data.totalPresent / (data.totalEmployees * totalWorkingDays)) * 100;
          return (
            <Grid item xs={12} sm={6} md={4} key={dept}>
              <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mb: 1 }}>
                    {dept}
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Employees:</Typography>
                      <Typography variant="body2" sx={{ color: '#FFFFFF' }}>{data.totalEmployees}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present Days:</Typography>
                      <Typography variant="body2" sx={{ color: '#81C784' }}>{data.totalPresent}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Late Days:</Typography>
                      <Typography variant="body2" sx={{ color: '#FFB74D' }}>{data.totalLate}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Hours:</Typography>
                      <Typography variant="body2" sx={{ color: '#D4AF37' }}>{formatWorkingHours(data.totalWorkingHours)}</Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={attendanceRate}
                        sx={{
                          bgcolor: '#2A3E24',
                          '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37' }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#B4C4A8', mt: 0.5, display: 'block' }}>
                        Attendance Rate: {attendanceRate.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Top Performers & Needing Attention */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #81C784', borderRadius: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp sx={{ color: '#81C784' }} />
                  <Typography sx={{ color: '#81C784', fontWeight: 600 }}>Top Performers</Typography>
                </Box>
              }
            />
            <CardContent>
              <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#D4AF37' }}>Employee</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Department</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Present</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Attendance %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topPerformers.map((emp, idx) => {
                      const attendancePercent = ((emp.daysPresent + emp.daysLate) / totalWorkingDays) * 100;
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {emp.employee?.first_name} {emp.employee?.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#B4C4A8' }}>{emp.employee?.department || '-'}</TableCell>
                          <TableCell>
                            <Chip label={emp.daysPresent || 0} size="small" sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784' }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={`${attendancePercent.toFixed(0)}%`} size="small" sx={{ bgcolor: alpha('#D4AF37', 0.2), color: '#D4AF37' }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #EF9A9A', borderRadius: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentLate sx={{ color: '#EF9A9A' }} />
                  <Typography sx={{ color: '#EF9A9A', fontWeight: 600 }}>Needs Attention</Typography>
                </Box>
              }
            />
            <CardContent>
              <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#D4AF37' }}>Employee</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Department</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Absent</TableCell>
                      <TableCell sx={{ color: '#D4AF37' }}>Late</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {needingAttention.map((emp, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                            {emp.employee?.first_name} {emp.employee?.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{emp.employee?.department || '-'}</TableCell>
                        <TableCell>
                          <Chip label={emp.daysAbsent || 0} size="small" sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={emp.daysLate || 0} size="small" sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Employee Summary Table */}
      <Typography variant="h6" sx={{ mt: 2, mb: 2, color: '#D4AF37' }}>
        Employee-wise Detailed Summary
      </Typography>
      <TableContainer component={Paper} sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#2A3E24' }}>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Present</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Late</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Absent</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">On Leave</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Working Hours</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Attendance %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.summary.map((emp, index) => {
              const presentDays = emp.daysPresent || 0;
              const lateDays = emp.daysLate || 0;
              const totalWorkDays = emp.totalWorkingDays || totalWorkingDays;
              const attendancePercent = totalWorkDays > 0 ? ((presentDays + lateDays) / totalWorkDays) * 100 : 0;
              
              return (
                <TableRow key={index} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                      {emp.employee?.first_name} {emp.employee?.last_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                      {emp.employee?.employee_id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#B4C4A8' }}>{emp.employee?.department || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={presentDays} size="small" sx={{ bgcolor: alpha('#4CAF50', 0.2), color: '#81C784' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={lateDays} size="small" sx={{ bgcolor: alpha('#FF9800', 0.2), color: '#FFB74D' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={emp.daysAbsent || 0} size="small" sx={{ bgcolor: alpha('#F44336', 0.2), color: '#EF9A9A' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={emp.leaves?.length || 0} size="small" sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: '#D4AF37', fontWeight: 500 }}>
                      {emp.totalWorkingHours || formatWorkingHours(0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={attendancePercent}
                        sx={{
                          width: 60,
                          bgcolor: '#2A3E24',
                          '& .MuiLinearProgress-bar': { bgcolor: attendancePercent >= 90 ? '#4CAF50' : attendancePercent >= 75 ? '#FF9800' : '#F44336' }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: attendancePercent >= 90 ? '#81C784' : attendancePercent >= 75 ? '#FFB74D' : '#EF9A9A' }}>
                        {attendancePercent.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyReportCard;